import type { ApiClient } from "../../api/client";
import type { Authorization, Order } from "../../api/types";
import { canDelete } from "./order-view";

/** What a delete needs besides the order: the owner's code and the special admin code from Tafo. */
export interface DeleteKeys {
  authorization: Authorization;
  adminCode: string;
}

/**
 * Deletes one order for good. An order in a closed Z goes with a reason: the Z is recomputed and the
 * removal is kept (ADR 0028).
 */
export async function deleteOne(api: ApiClient, order: Order, keys: DeleteKeys, reason: string): Promise<void> {
  const sent = { staffToken: keys.authorization.token, headers: { "x-admin-code": keys.adminCode } };
  if (order.zNumber === null) await api.request("DELETE", `/orders/${order.id}`, sent);
  else await api.request("POST", `/orders/${order.id}/removal`, { ...sent, body: { reason } });
}

/** The shortest reason the server takes for an order in a closed Z. */
export const MIN_REASON = 5;
/** The terms (in-app text b): at most this many deleted orders a day. */
export const DAILY_LIMIT = 5;

/** The chosen orders, sorted by what can happen to them. */
export function sortForDelete(orders: readonly Order[]): {
  /** Can be deleted (the shop's own, unpaid or cash). */
  deletable: Order[];
  /** Never: paid by card or QR, or an online order. They stay, and the window says so. */
  refused: Order[];
  /** Of the deletable ones: in a closed Z, so a reason is asked. */
  afterZ: Order[];
} {
  const deletable = orders.filter(canDelete);
  return {
    deletable,
    refused: orders.filter((order) => !canDelete(order)),
    afterZ: deletable.filter((order) => order.zNumber !== null),
  };
}
